// Ops Cameras panel — renders live YouTube-embedded webcam tiles for ports,
// canals, traffic, and marine weather. The 2-column tile grid pattern and
// dark "live status wall" aesthetic are inspired by World Monitor (AGPL-3.0;
// see /NOTICE). All iframe/embed code here is original to SupplyLens.
import { Panel } from "../Panel"
import type { StreamTile } from "../dashboard-types"

const KIND_LABEL: Record<string, string> = {
  port: "PORT",
  canal: "CANAL",
  traffic: "TRAFFIC",
  weather: "WEATHER",
  webcam: "CAM",
  youtube: "LIVE",
}

const DEFAULT_BADGE = "bg-slate-500/20 text-slate-300 border-slate-500/40"

const KIND_BADGE: Record<string, string> = {
  port: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  canal: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
  traffic: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  weather: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  webcam: DEFAULT_BADGE,
  youtube: "bg-red-500/20 text-red-300 border-red-500/40",
}

function badgeClass(kind: string): string {
  return KIND_BADGE[kind] ?? DEFAULT_BADGE
}

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind.toUpperCase()
}

/* Sample headlines for the demo ticker. These are NOT a live news feed \u2014
   the ticker is explicitly tagged DEMO until a real intelligence source is
   wired in. */
const TICKER_HEADLINES = [
  "Red Sea transit risk elevated \u2014 carriers diverting via Cape",
  "LA/LB port backlog now 36 hours",
  "Baltic Dry Index breaks 1,450 resistance",
  "Panama Canal water levels recovering",
]

export function StreamGrid({ streams, demo }: { streams: StreamTile[]; demo?: boolean }) {
  return (
    <Panel title="Ops Cameras" live demo={demo}>
      <div className="relative h-full">
        <div
          className="grid grid-cols-2 gap-0 h-full"
          style={{ gridAutoRows: "minmax(150px, 1fr)" }}
        >
          {streams.map((s) => (
            <StreamCell key={s.id} stream={s} />
          ))}
        </div>
        {/* News ticker overlay \u2014 sample headlines, explicitly tagged demo. */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-zinc-900/95 border-t border-amber-500/30 flex items-center overflow-hidden z-10">
          <span
            className="shrink-0 text-[10px] font-bold text-amber-300 uppercase tracking-wider px-2 bg-amber-500/15 h-full flex items-center border-r border-amber-500/30"
            title="Sample headlines \u2014 not a live news feed"
          >
            demo &middot;
          </span>
          <div className="flex-1 overflow-hidden relative">
            <div className="ticker-scroll whitespace-nowrap text-[10px] text-zinc-300 uppercase tracking-wider">
              {TICKER_HEADLINES.map((h, i) => (
                <span key={i}>
                  {h}
                  {i < TICKER_HEADLINES.length - 1 ? " \u00A0\u00A0\u00A0\u2022\u00A0\u00A0\u00A0 " : ""}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Panel>
  )
}

function StreamCell({ stream }: { stream: StreamTile }) {
  const hasStream = Boolean(stream.url)
  return (
    <div
      className="relative border-r border-b border-[color:var(--border)] bg-[color:var(--map-bg)] overflow-hidden"
      style={{ aspectRatio: "16 / 9", minHeight: 150 }}
    >
      {hasStream && stream.url ? (
        <iframe
          src={stream.url}
          title={stream.label}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
        />
      ) : (
        <StreamPlaceholder label={stream.label} kind={stream.kind} />
      )}

      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-1.5">
        <span
          className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border backdrop-blur-sm ${badgeClass(stream.kind)}`}
        >
          {kindLabel(stream.kind)}
        </span>
        {hasStream && (
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/60 text-emerald-300 border border-emerald-500/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            live
          </span>
        )}
      </div>

      {hasStream && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent">
          <div className="text-[10px] font-semibold text-[color:var(--text)] truncate">
            {stream.label}
          </div>
          {(stream.location || stream.source) && (
            <div className="text-[9px] text-[color:var(--text-dim)] truncate">
              {stream.location ?? ""}
              {stream.location && stream.source ? " · " : ""}
              {stream.source ?? ""}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StreamPlaceholder({ label, kind }: { label: string; kind: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(34,211,238,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(250,204,21,0.08), transparent 40%)",
        }}
      />
      <div className="relative text-center px-2">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">
          {kindLabel(kind)}
        </div>
        <div className="text-sm text-[color:var(--text)] mt-0.5 font-semibold">
          {label}
        </div>
        <div className="text-[9px] text-amber-300/80 mt-0.5 flex items-center justify-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-300/80" /> stream
          pending
        </div>
      </div>
    </div>
  )
}
